import { convexQuery } from "@convex-dev/react-query";
import { api } from "@selca/backend/api";
import { useQuery } from "@tanstack/react-query";
import { useConvexAuth } from "convex/react";
import { authHooks } from "../lib/auth-client";

/**
 * 🔐 Unified auth state using better-auth's useSession as source of truth
 *
 * - Uses authClient.useSession() for reactive session state (auto-syncs on sign-out)
 * - Uses Convex query for additional profile data (phone number, etc.)
 * - No manual localStorage caching - TanStack Query handles caching
 */
export function useAuth() {
  // better-auth-tanstack's useSession is the source of truth for auth state
  // Uses TanStack Query under the hood, respects refetchOnWindowFocus: false
  const {
    data: session,
    isPending: isSessionPending,
    isRefetching,
  } = authHooks.useSession();

  const { isLoading: isConvexLoading } = useConvexAuth();
  const isAuthenticated = !!session;

  // Fetch additional profile data from Convex (e.g., phone number)
  const { data: profile, isLoading: isProfileLoading } = useQuery({
    ...convexQuery(api.users.getMyProfile, {}),
    enabled: isAuthenticated && !isSessionPending,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // User comes from session (source of truth for auth state)
  // Profile only provides additional Convex data (e.g., phoneNumber)
  const user = session?.user;
  const isPhoneLinked = !!profile?.phoneNumber;

  // Show loading state when session is pending and we have no data yet
  const isPending = isSessionPending && !session;

  return {
    user,
    session,
    profile,
    isPending: isPending || isRefetching,
    isAuthLoading: isConvexLoading,
    isAuthenticated,
    isPhoneLinked,
    isFullySetUp: isAuthenticated && isPhoneLinked,
    isProfileLoading,
  };
}
